'use client';

import { c as _c } from "react/compiler-runtime";
import type { OrderedFiles } from '@/lib/ai/ordered-files-service/order-files-prompts';
import type { FileReviewDecision, FileReviewDecisionGlyphChar } from '@/lib/pr-v2/file-review-decisions';
import type { PrV2CommentResponse, PrV2ReviewThread } from '@/lib/pr-v2/types';
import type { File, FileStatus } from '@/types/github';
import { Check, ChevronDown, ChevronRight, Folder, FolderTree, Search, X } from 'lucide-react';
import type { ComponentProps, MouseEvent as ReactMouseEvent, ReactNode, PointerEvent as ReactPointerEvent } from 'react';
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import type { ListImperativeAPI, RowComponentProps } from 'react-window';
import { List, useListCallbackRef } from 'react-window';
import { useMediaQuery } from 'usehooks-ts';
import type { PrV2VersionOption } from '../diff/hooks/use-pr-v2-version-comparison';
import type { PrV2CollapsibleTreeNode } from './pr-v2-file-tree-types';
import type { PrV2FileTreeOpenBreakpoint, PrV2GroupedCodeContent } from './pr-v2-file-tree-utils';
import { PrV2DecisionBadge } from '../decisions/pr-v2-decision-badge';
import { isPrV2HotkeyTextEntryTarget } from '../diff/hooks/use-pr-v2-hotkey-interactive-target';
import { PrV2VersionComparisonPicker } from '../diff/pr-v2-version-comparison-picker';
import { PrV2FileTypeIcon } from '../shared/pr-v2-file-type-icon';
import { PR_V2_TOOLBAR_ICON_BUTTON_CLASSNAME, PR_V2_TOOLBAR_TEXT_BUTTON_CLASSNAME } from '../shared/pr-v2-toolbar-styles';
import { PR_V2_TOOLTIP_TEXT_CLASSNAME } from '../shared/pr-v2-tooltip-styles';
import { collapseSingleChildFolderChain } from './pr-v2-file-tree-collapse';
import { clampFileTreeWidth, getPrV2ChapterKey, getPrV2GroupElementId, PR_V2_FILE_TREE_FONT_SIZE_PX, PR_V2_FILE_TREE_OPEN_MEDIA_QUERY, splitPrV2FileTreeFileName, splitPrV2FileTreeFolderLabel } from './pr-v2-file-tree-utils';
import { usePrV2FileTreeFolderCollapse, usePrV2FileTreeSearchCursor } from './use-pr-v2-file-tree-search';
import { usePrV2FileTreeState } from './use-pr-v2-file-tree-state';
import { usePrV2UnresolvedThreadCounts } from './use-pr-v2-unresolved-thread-counts';
import { KeyboardShortcut } from '@/components/ui/keyboard-shortcut';
import { Progress } from '@/components/ui/progress';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { BetterTooltip, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { WorkingDots } from '@/components/ui/working-dots';
import { formatFileReviewDecisionLabel, getFileReviewDecisionGlyphChar } from '@/lib/pr-v2/file-review-decisions';
import { getPrV2ActiveFileStoreKey, usePrV2ActiveFileStore } from '@/lib/stores/navigation/pr-v2-active-file-store';
import { usePrV2ViewPreferencesStore } from '@/lib/stores/pr-v2-view-preferences-store';
import { useZenModeStore } from '@/lib/stores/zen-mode-store';
import { cn } from '@/lib/utils';
import { FILE_STATUS } from '@/types/github';
type PrV2FileTreeProps = {
  desktopSide?: 'left' | 'right';
  /** A visible toolbar host; omit it when the drawer needs its floating opener instead. */
  toolbarHost?: HTMLElement | null;
  /** The breakpoint from which the desktop tree starts open, until the user toggles it. */
  openFrom: PrV2FileTreeOpenBreakpoint;
  /** The diff host resolves pane width and viewport once for both the code and the tree. */
  layout: 'sidebar' | 'left-drawer' | 'right-drawer';
  files: File[];
  orderedFiles: OrderedFiles[];
  groupedCodeContent: PrV2GroupedCodeContent[];
  hasAiFileGrouping: boolean;
  owner: string;
  repo: string;
  number: string;
  fileReviewDecisionsByFilename: Map<string, FileReviewDecision[]>;
  versionOptions: PrV2VersionOption[];
  selectedBaseSha: string | null;
  selectedHeadSha: string | null;
  isComparisonLoading: boolean;
  isOrderedFilesLoading: boolean;
  hasMeaningfulComparison: boolean;
  isCustomComparison: boolean;
  hasUserComparisonOverride: boolean;
  lastReviewedVersionSha: string | null;
  lastReviewedSubmittedAt: string | null;
  setComparisonAction: (comparison: {
    baseSha: string;
    headSha: string;
  } | null) => void;
  resetComparisonAction: () => void;
  versionUpdate?: {
    currentLabel: string;
    latestLabel: string;
    onReviewLatest: () => void;
  };
  comments: PrV2CommentResponse[];
  reviewThreads: PrV2ReviewThread[];
  viewedFilenames: Set<string>;
  /** Files marked viewed before a push that changed them. */
  changedSinceLastViewFilenames: ReadonlySet<string>;
  chapterModeEnabled?: boolean;
  activeChapterKey?: string | null;
  getChapterKeyForFilename?: (filename: string) => string | null;
  selectChapterAction?: (chapterKey: string) => void;
  selectFileAction: (filename: string) => void;
  selectGroupAction: (groupId: string) => void;
};
type TreeFileRecord = {
  filename: string;
  basename: string;
  status: FileStatus;
  isViewed: boolean;
  isChangedSinceLastView: boolean;
  groupName: string;
  groupOrder: number;
  chapterKey: string;
  sectionIndex: number;
  groupNameReason?: string;
  orderNumber: number;
  unresolvedThreads: number;
  hasReviewDecision: boolean;
  reviewDecisionTitle?: string;
  reviewDecisionGlyph?: FileReviewDecisionGlyphChar;
};
type TreeNode = PrV2CollapsibleTreeNode<TreeFileRecord>;
type GroupRow = {
  id: string;
  kind: 'group';
  groupId: string;
  chapterKey: string;
  sectionIndex: number;
  label: string;
  groupReason?: string;
};
type GroupSpacerRow = {
  id: string;
  kind: 'group-spacer';
  sectionIndex: number;
};
type FolderRow = {
  id: string;
  kind: 'folder';
  folderPath: string;
  fullPath: string;
  label: string;
  depth: number;
  isCollapsed: boolean;
};
type FileRow = {
  id: string;
  kind: 'file';
  filename: string;
  chapterKey: string;
  label: string;
  depth: number;
  status: FileStatus;
  isViewed: boolean;
  isChangedSinceLastView: boolean;
  unresolvedThreads: number;
  hasReviewDecision: boolean;
  reviewDecisionTitle?: string;
  reviewDecisionGlyph?: FileReviewDecisionGlyphChar;
};
type TreeRow = GroupRow | GroupSpacerRow | FolderRow | FileRow;
const ROW_HEIGHT = 26;
const TREE_DEFAULT_HEIGHT_PX = 800;
/** Vertical gap between grouped sections (no rule; spacer row only). */
const GROUP_SECTION_GAP_PX = 20;
/** How long the tree ignores the current file after the user scrolls the tree themselves. */
const TREE_SCROLL_INPUT_HOLD_MS = 1000;
const TREE_ROW_BASE_PADDING_LEFT_PX = 8;
const TREE_ROW_DEPTH_PADDING_LEFT_PX = 6;
// Aligns file row label with folder row label (folder has chevron + icon before label, file has icon only).
const TREE_FILE_ROW_VISUAL_ALIGNMENT_OFFSET_PX = 11;
const FILE_TREE_ICON_COLOR_CLASS = 'text-zinc-500';
// Added, deleted and renamed files color their name and end their row with a status letter, as
// Pierre's tree and VS Code do, so the status also reads without color. Deleted names are also
// struck through.
const FILE_ROW_STATUS: Partial<Record<FileStatus, {
  letter: string;
  label: string;
  className: string;
}>> = {
  [FILE_STATUS.ADDED]: {
    letter: 'A',
    label: 'Added file',
    className: 'text-emerald-400'
  },
  [FILE_STATUS.REMOVED]: {
    letter: 'D',
    label: 'Deleted file',
    className: 'text-rose-400'
  },
  [FILE_STATUS.RENAMED]: {
    letter: 'R',
    label: 'Renamed file',
    className: 'text-amber-400'
  }
};

/**
 * A tree row label whose head truncates while its tail (a suffix or last folder) stays whole,
 * unless the tail alone is wider than the label.
 * Spreads the rest of its props because it is a tooltip trigger child.
 */
function TreeRowLabel(t0) {
  const $ = _c(15);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      label: t1,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const {
    head,
    tail
  } = t1;
  let t2;
  if ($[4] !== className) {
    t2 = cn("flex min-w-0 flex-1", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== head) {
    t3 = <span className="min-w-0 truncate">{head}</span>;
    $[6] = head;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== tail) {
    t4 = tail ? <span className="max-w-full shrink-0 truncate">{tail}</span> : null;
    $[8] = tail;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== props || $[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = <span {...props} className={t2}>{t3}{t4}</span>;
    $[10] = props;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function getBasename(filename: string): string {
  const parts = filename.split('/');
  return parts[parts.length - 1] || filename;
}
function createTreeNode(): TreeNode {
  return {
    folders: new Map<string, TreeNode>(),
    files: []
  };
}
function renderGroupTooltipContent(title: string, reason: string | undefined) {
  return <div className="max-w-[22rem]">
      <span className="sr-only">{reason ? `${title} ${reason}` : title}</span>
      <p className="mb-2 text-sm font-semibold text-zinc-100">{title}</p>
      {reason ? <p className={PR_V2_TOOLTIP_TEXT_CLASSNAME}>{reason}</p> : null}
    </div>;
}
function buildTree(records: TreeFileRecord[]): TreeNode {
  const root = createTreeNode();
  records.forEach(record => {
    const parts = record.filename.split('/');
    const folderParts = parts.slice(0, -1);
    let current = root;
    folderParts.forEach(part => {
      const existing = current.folders.get(part);
      if (existing) {
        current = existing;
        return;
      }
      const next = createTreeNode();
      current.folders.set(part, next);
      current = next;
    });
    current.files.push(record);
  });
  return root;
}
function sortTreeNode(node: TreeNode) {
  node.files.sort((a, b) => a.filename.localeCompare(b.filename));
  const sortedFolders = Array.from(node.folders.entries()).sort(([a], [b]) => a.localeCompare(b));
  node.folders = new Map(sortedFolders);
  sortedFolders.forEach(([, child]) => {
    sortTreeNode(child);
  });
}
type VirtualizedRowProps = {
  rows: TreeRow[];
  activeFileStoreKey: string;
  searchCursorFilename: string | null;
  isInteractionDisabled: boolean;
  chapterModeEnabled: boolean;
  activeChapterKey: string | null;
  onSelectFile: (filename: string) => void;
  onSelectGroup: (groupId: string) => void;
  onSelectChapter: (chapterKey: string) => void;
  toggleFolder: (folderPath: string) => void;
};
function getPrV2FileTreeVirtualRowHeight(index: number, props: VirtualizedRowProps): number {
  const row = props.rows[index];
  if (row?.kind === 'group-spacer') {
    return GROUP_SECTION_GAP_PX;
  }
  return ROW_HEIGHT;
}
function getPrV2FileTreeRowKey(index: number, props: VirtualizedRowProps): string | number {
  return props.rows[index]?.id ?? index;
}
function GroupRowContent(t0) {
  const $ = _c(30);
  const {
    row,
    isInteractionDisabled,
    chapterModeEnabled,
    activeChapterKey,
    onSelectGroup,
    onSelectChapter
  } = t0;
  const isActiveChapter = chapterModeEnabled && activeChapterKey === row.chapterKey;
  const t1 = `pr-v2-file-tree-group-button-${row.sectionIndex}`;
  const t2 = !isActiveChapter && !isInteractionDisabled && "hover:bg-zinc-900";
  const t3 = isActiveChapter && "bg-zinc-900";
  const t4 = isInteractionDisabled && "cursor-not-allowed opacity-60";
  let t5;
  if ($[0] !== t2 || $[1] !== t3 || $[2] !== t4) {
    t5 = cn("h-full min-w-0 flex-1 rounded px-2 text-left font-medium text-zinc-100", t2, t3, t4);
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== chapterModeEnabled || $[5] !== onSelectChapter || $[6] !== onSelectGroup || $[7] !== row.chapterKey || $[8] !== row.groupId) {
    t6 = () => {
      if (chapterModeEnabled) {
        onSelectChapter(row.chapterKey);
      }
      onSelectGroup(row.groupId);
    };
    $[4] = chapterModeEnabled;
    $[5] = onSelectChapter;
    $[6] = onSelectGroup;
    $[7] = row.chapterKey;
    $[8] = row.groupId;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = `pr-v2-file-tree-group-index-${row.sectionIndex}`;
  const t8 = `${row.sectionIndex}.`;
  let t9;
  if ($[10] !== t7 || $[11] !== t8) {
    t9 = <span data-testid={t7} className="shrink-0 text-zinc-500">{t8}</span>;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== row.groupReason || $[14] !== row.label) {
    t10 = renderGroupTooltipContent(row.label, row.groupReason);
    $[13] = row.groupReason;
    $[14] = row.label;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== row.label) {
    t11 = <span className="min-w-0 flex-1 truncate text-left">{row.label}</span>;
    $[16] = row.label;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t10 || $[19] !== t11) {
    t12 = <BetterTooltip content={t10} contentClassName="ml-5 px-4 py-3">{t11}</BetterTooltip>;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t12 || $[22] !== t9) {
    t13 = <span className="flex min-w-0 items-baseline gap-2">{t9}{t12}</span>;
    $[21] = t12;
    $[22] = t9;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== isInteractionDisabled || $[25] !== t1 || $[26] !== t13 || $[27] !== t5 || $[28] !== t6) {
    t14 = <div className="relative flex h-full items-center gap-1"><button type="button" data-pr-v2-file-nav-hotkey-target="true" data-testid={t1} className={t5} disabled={isInteractionDisabled} onClick={t6}>{t13}</button></div>;
    $[24] = isInteractionDisabled;
    $[25] = t1;
    $[26] = t13;
    $[27] = t5;
    $[28] = t6;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
}
function RowContent(t0) {
  const $ = _c(43);
  const {
    row,
    activeFileStoreKey,
    searchCursorFilename,
    isInteractionDisabled,
    chapterModeEnabled,
    activeChapterKey,
    onSelectFile,
    onSelectGroup,
    onSelectChapter,
    toggleFolder
  } = t0;
  if (row.kind === "group") {
    let t1;
    if ($[0] !== activeChapterKey || $[1] !== chapterModeEnabled || $[2] !== isInteractionDisabled || $[3] !== onSelectChapter || $[4] !== onSelectGroup || $[5] !== row) {
      t1 = <GroupRowContent row={row} isInteractionDisabled={isInteractionDisabled} chapterModeEnabled={chapterModeEnabled} activeChapterKey={activeChapterKey} onSelectGroup={onSelectGroup} onSelectChapter={onSelectChapter} />;
      $[0] = activeChapterKey;
      $[1] = chapterModeEnabled;
      $[2] = isInteractionDisabled;
      $[3] = onSelectChapter;
      $[4] = onSelectGroup;
      $[5] = row;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    return t1;
  }
  if (row.kind === "group-spacer") {
    const t1 = `pr-v2-file-tree-group-separator-${row.sectionIndex}`;
    let t2;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        height: GROUP_SECTION_GAP_PX
      };
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== t1) {
      t3 = <div data-testid={t1} className="shrink-0" style={t2} aria-hidden="true" />;
      $[8] = t1;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  if (row.kind === "folder") {
    const t1 = !isInteractionDisabled && "hover:bg-zinc-900";
    const t2 = isInteractionDisabled && "cursor-not-allowed opacity-60";
    let t3;
    if ($[10] !== t1 || $[11] !== t2) {
      t3 = cn("flex h-full w-full items-center gap-1 rounded px-2 text-left text-zinc-300", t1, t2);
      $[10] = t1;
      $[11] = t2;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    const t4 = TREE_ROW_BASE_PADDING_LEFT_PX + row.depth * TREE_ROW_DEPTH_PADDING_LEFT_PX;
    let t5;
    if ($[13] !== t4) {
      t5 = {
        paddingLeft: t4
      };
      $[13] = t4;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    let t6;
    if ($[15] !== row.folderPath || $[16] !== toggleFolder) {
      t6 = () => toggleFolder(row.folderPath);
      $[15] = row.folderPath;
      $[16] = toggleFolder;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    let t7;
    if ($[18] !== row.isCollapsed) {
      t7 = row.isCollapsed ? <ChevronRight className="size-3.5 shrink-0 text-zinc-500" /> : <ChevronDown className="size-3.5 shrink-0 text-zinc-500" />;
      $[18] = row.isCollapsed;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <Folder className={cn("size-3.5 shrink-0", FILE_TREE_ICON_COLOR_CLASS)} fill="currentColor" strokeWidth={0} />;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    const t9 = row.fullPath;
    let t10;
    if ($[21] !== row.label) {
      t10 = splitPrV2FileTreeFolderLabel(row.label);
      $[21] = row.label;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    let t11;
    if ($[23] !== t10) {
      t11 = <TreeRowLabel label={t10} />;
      $[23] = t10;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    let t12;
    if ($[25] !== row.fullPath || $[26] !== t11) {
      t12 = <BetterTooltip content={t9}>{t11}</BetterTooltip>;
      $[25] = row.fullPath;
      $[26] = t11;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    let t13;
    if ($[28] !== isInteractionDisabled || $[29] !== t12 || $[30] !== t3 || $[31] !== t5 || $[32] !== t6 || $[33] !== t7) {
      t13 = <button type="button" data-pr-v2-file-nav-hotkey-target="true" className={t3} style={t5} disabled={isInteractionDisabled} onClick={t6}>{t7}{t8}{t12}</button>;
      $[28] = isInteractionDisabled;
      $[29] = t12;
      $[30] = t3;
      $[31] = t5;
      $[32] = t6;
      $[33] = t7;
      $[34] = t13;
    } else {
      t13 = $[34];
    }
    return t13;
  }
  let t1;
  if ($[35] !== activeFileStoreKey || $[36] !== chapterModeEnabled || $[37] !== isInteractionDisabled || $[38] !== onSelectChapter || $[39] !== onSelectFile || $[40] !== row || $[41] !== searchCursorFilename) {
    t1 = <FileRowContent row={row} activeFileStoreKey={activeFileStoreKey} searchCursorFilename={searchCursorFilename} isInteractionDisabled={isInteractionDisabled} chapterModeEnabled={chapterModeEnabled} onSelectChapter={onSelectChapter} onSelectFile={onSelectFile} />;
    $[35] = activeFileStoreKey;
    $[36] = chapterModeEnabled;
    $[37] = isInteractionDisabled;
    $[38] = onSelectChapter;
    $[39] = onSelectFile;
    $[40] = row;
    $[41] = searchCursorFilename;
    $[42] = t1;
  } else {
    t1 = $[42];
  }
  return t1;
}
function FileRowContent(t0) {
  const $ = _c(56);
  const {
    row,
    activeFileStoreKey,
    searchCursorFilename,
    isInteractionDisabled,
    chapterModeEnabled,
    onSelectChapter,
    onSelectFile
  } = t0;
  let t1;
  if ($[0] !== activeFileStoreKey || $[1] !== row.filename) {
    t1 = state => state.activeFilenameByPr[activeFileStoreKey] === row.filename;
    $[0] = activeFileStoreKey;
    $[1] = row.filename;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isCurrentFile = usePrV2ActiveFileStore(t1);
  const status = FILE_ROW_STATUS[row.status];
  const t2 = !isCurrentFile && !isInteractionDisabled && "hover:bg-zinc-900";
  const t3 = isCurrentFile && "bg-zinc-800";
  const t4 = isCurrentFile && "text-zinc-100";
  const t5 = searchCursorFilename === row.filename && "ring-1 ring-zinc-500 ring-inset";
  const t6 = isInteractionDisabled && "cursor-not-allowed opacity-60";
  let t7;
  if ($[3] !== t2 || $[4] !== t3 || $[5] !== t4 || $[6] !== t5 || $[7] !== t6) {
    t7 = cn("flex h-full w-full items-center gap-1 rounded px-2 text-left text-zinc-300", t2, t3, t4, t5, t6);
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = TREE_ROW_BASE_PADDING_LEFT_PX + row.depth * TREE_ROW_DEPTH_PADDING_LEFT_PX + TREE_FILE_ROW_VISUAL_ALIGNMENT_OFFSET_PX;
  let t9;
  if ($[9] !== t8) {
    t9 = {
      paddingLeft: t8
    };
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== chapterModeEnabled || $[12] !== onSelectChapter || $[13] !== onSelectFile || $[14] !== row.chapterKey || $[15] !== row.filename) {
    t10 = () => {
      if (chapterModeEnabled) {
        onSelectChapter(row.chapterKey);
      }
      onSelectFile(row.filename);
    };
    $[11] = chapterModeEnabled;
    $[12] = onSelectChapter;
    $[13] = onSelectFile;
    $[14] = row.chapterKey;
    $[15] = row.filename;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = row.filename;
  const t12 = isCurrentFile ? "true" : undefined;
  let t13;
  if ($[17] !== row.filename) {
    t13 = <PrV2FileTypeIcon filename={row.filename} className="size-3.5 shrink-0" />;
    $[17] = row.filename;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const t14 = row.filename;
  let t15;
  if ($[19] !== row.label) {
    t15 = splitPrV2FileTreeFileName(row.label);
    $[19] = row.label;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const t16 = status?.className;
  const t17 = row.status === FILE_STATUS.REMOVED && "line-through";
  let t18;
  if ($[21] !== t16 || $[22] !== t17) {
    t18 = cn(t16, t17);
    $[21] = t16;
    $[22] = t17;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== t15 || $[25] !== t18) {
    t19 = <TreeRowLabel label={t15} className={t18} />;
    $[24] = t15;
    $[25] = t18;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] !== row.filename || $[28] !== t19) {
    t20 = <BetterTooltip content={t14}>{t19}</BetterTooltip>;
    $[27] = row.filename;
    $[28] = t19;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== status) {
    t21 = status ? <span role="img" aria-label={status.label} className={cn("w-3 shrink-0 text-center font-semibold", status.className)}>{status.letter}</span> : null;
    $[30] = status;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] !== row.hasReviewDecision || $[33] !== row.reviewDecisionGlyph || $[34] !== row.reviewDecisionTitle) {
    t22 = row.hasReviewDecision ? <PrV2DecisionBadge label={row.reviewDecisionTitle ?? "Review decision"} glyph={row.reviewDecisionGlyph} className="size-4" /> : null;
    $[32] = row.hasReviewDecision;
    $[33] = row.reviewDecisionGlyph;
    $[34] = row.reviewDecisionTitle;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== row.hasReviewDecision || $[37] !== row.isChangedSinceLastView || $[38] !== row.isViewed || $[39] !== row.unresolvedThreads) {
    t23 = row.isViewed ? <Check className={cn("size-3.5 shrink-0 text-zinc-500", row.unresolvedThreads === 0 && !row.hasReviewDecision && "ml-auto")} aria-hidden="true" /> : row.isChangedSinceLastView ? <Tooltip><TooltipTrigger asChild={true}><span role="img" aria-label="Changed since you marked it viewed" className={cn("flex size-3.5 shrink-0 items-center justify-center", row.unresolvedThreads === 0 && !row.hasReviewDecision && "ml-auto")}><span className="size-1.5 rounded-full bg-zinc-400" /></span></TooltipTrigger><TooltipContent side="right" className={PR_V2_TOOLTIP_TEXT_CLASSNAME}>Changed since you marked it viewed</TooltipContent></Tooltip> : null;
    $[36] = row.hasReviewDecision;
    $[37] = row.isChangedSinceLastView;
    $[38] = row.isViewed;
    $[39] = row.unresolvedThreads;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== row.unresolvedThreads) {
    t24 = row.unresolvedThreads > 0 ? <Tooltip><TooltipTrigger asChild={true}><span className="ml-auto inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-sm bg-zinc-700 px-1 text-[10px] text-zinc-100">{row.unresolvedThreads}</span></TooltipTrigger><TooltipContent side="right" className={PR_V2_TOOLTIP_TEXT_CLASSNAME}>{`${row.unresolvedThreads} unresolved ${row.unresolvedThreads === 1 ? "thread" : "threads"}`}</TooltipContent></Tooltip> : null;
    $[41] = row.unresolvedThreads;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== isInteractionDisabled || $[44] !== row.filename || $[45] !== t10 || $[46] !== t12 || $[47] !== t13 || $[48] !== t20 || $[49] !== t21 || $[50] !== t22 || $[51] !== t23 || $[52] !== t24 || $[53] !== t7 || $[54] !== t9) {
    t25 = <button type="button" data-pr-v2-file-nav-hotkey-target="true" className={t7} style={t9} disabled={isInteractionDisabled} onClick={t10} data-filename={t11} aria-current={t12}>{t13}{t20}{t21}{t22}{t23}{t24}</button>;
    $[43] = isInteractionDisabled;
    $[44] = row.filename;
    $[45] = t10;
    $[46] = t12;
    $[47] = t13;
    $[48] = t20;
    $[49] = t21;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
    $[53] = t7;
    $[54] = t9;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  return t25;
}
function VirtualizedRow(t0) {
  const $ = _c(13);
  let ariaAttributes;
  let index;
  let rowProps;
  let rows;
  let style;
  if ($[0] !== t0) {
    ({
      ariaAttributes,
      index,
      rows,
      style,
      ...rowProps
    } = t0);
    $[0] = t0;
    $[1] = ariaAttributes;
    $[2] = index;
    $[3] = rowProps;
    $[4] = rows;
    $[5] = style;
  } else {
    ariaAttributes = $[1];
    index = $[2];
    rowProps = $[3];
    rows = $[4];
    style = $[5];
  }
  const row = rows[index];
  if (!row) {
    return null;
  }
  let t1;
  if ($[6] !== row || $[7] !== rowProps) {
    t1 = <RowContent row={row} {...rowProps} />;
    $[6] = row;
    $[7] = rowProps;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== ariaAttributes || $[10] !== style || $[11] !== t1) {
    t2 = <div style={style} className="px-2" {...ariaAttributes}>{t1}</div>;
    $[9] = ariaAttributes;
    $[10] = style;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
}

// Sticky toolbar buttons can scroll the PR container when the browser focuses them.
function preventToolbarMouseFocus(event: ReactMouseEvent<HTMLButtonElement>) {
  event.preventDefault();
}

/** Shows or hides the file tree in both states, lit while the tree is open. */
function FileTreeToggle(t0) {
  const $ = _c(15);
  const {
    isCollapsed,
    isDisabled,
    onToggle
  } = t0;
  const label = isCollapsed ? "Show file tree" : "Hide file tree";
  const t1 = !isCollapsed && "bg-zinc-800 text-zinc-100 hover:bg-zinc-800";
  const t2 = isDisabled && "cursor-not-allowed opacity-60 hover:bg-transparent";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = cn(PR_V2_TOOLBAR_ICON_BUTTON_CLASSNAME, t1, t2);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <FolderTree className="size-3.5" />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== isDisabled || $[5] !== label || $[6] !== onToggle || $[7] !== t3) {
    t5 = <TooltipTrigger asChild={true}><button type="button" className={t3} disabled={isDisabled} onMouseDown={preventToolbarMouseFocus} onClick={onToggle} aria-label={label}>{t4}</button></TooltipTrigger>;
    $[4] = isDisabled;
    $[5] = label;
    $[6] = onToggle;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <KeyboardShortcut>F</KeyboardShortcut>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== label) {
    t7 = <TooltipContent side="top"><div className="flex items-center gap-2">{label}{t6}</div></TooltipContent>;
    $[10] = label;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t5 || $[13] !== t7) {
    t8 = <Tooltip>{t5}{t7}</Tooltip>;
    $[12] = t5;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
}
export function PrV2FileTree(t0) {
  const $ = _c(217);
  const {
    desktopSide: t1,
    toolbarHost: t2,
    openFrom,
    layout,
    files,
    orderedFiles,
    groupedCodeContent,
    hasAiFileGrouping,
    owner,
    repo,
    number,
    fileReviewDecisionsByFilename,
    versionOptions,
    selectedBaseSha,
    selectedHeadSha,
    isComparisonLoading,
    isOrderedFilesLoading,
    hasMeaningfulComparison,
    hasUserComparisonOverride,
    lastReviewedVersionSha,
    lastReviewedSubmittedAt,
    setComparisonAction,
    resetComparisonAction,
    versionUpdate,
    comments,
    reviewThreads,
    viewedFilenames,
    changedSinceLastViewFilenames,
    chapterModeEnabled: t3,
    activeChapterKey: t4,
    getChapterKeyForFilename,
    selectChapterAction,
    selectFileAction,
    selectGroupAction
  } = t0;
  const desktopSide = t1 === undefined ? "left" : t1;
  const toolbarHost = t2 === undefined ? null : t2;
  const chapterModeEnabled = t3 === undefined ? false : t3;
  const activeChapterKey = t4 === undefined ? null : t4;
  const isDrawerLayout = layout !== "sidebar";
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      initializeWithValue: false
    };
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const isWideScreen = useMediaQuery(PR_V2_FILE_TREE_OPEN_MEDIA_QUERY[openFrom], t5);
  const {
    isCollapsed,
    toggleCollapsed,
    viewMode,
    toggleViewMode
  } = usePrV2FileTreeState(!isWideScreen);
  const isZenMode = useZenModeStore(_temp);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const unresolvedThreadCountByFile = usePrV2UnresolvedThreadCounts(reviewThreads, comments);
  const hasComparisonPicker = hasMeaningfulComparison && versionOptions.length > 0;
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const searchInputRef = useRef(null);
  const resizeWidthRef = useRef(null);
  const releaseDraggedWidthRafRef = useRef(null);
  const treeWidthWrapperRef = useRef(null);
  const desktopToolbarRef = useRef(null);
  const isDesktopRightSide = desktopSide === "right";
  const fileTreeWidthPx = usePrV2ViewPreferencesStore(_temp2);
  const applyViewPreferences = usePrV2ViewPreferencesStore(_temp3);
  const [draggedTreeWidthPx, setDraggedTreeWidthPx] = useState(null);
  let t6;
  if ($[1] !== files) {
    t6 = new Map(files.map(_temp4));
    $[1] = files;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const statusByFilename = t6;
  let t7;
  if ($[3] !== orderedFiles) {
    t7 = new Map(orderedFiles.map(_temp5));
    $[3] = orderedFiles;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const orderedByFilename = t7;
  let t8;
  if ($[5] !== changedSinceLastViewFilenames || $[6] !== fileReviewDecisionsByFilename || $[7] !== getChapterKeyForFilename || $[8] !== groupedCodeContent || $[9] !== orderedByFilename || $[10] !== statusByFilename || $[11] !== unresolvedThreadCountByFile || $[12] !== viewedFilenames) {
    let t9;
    if ($[14] !== changedSinceLastViewFilenames || $[15] !== fileReviewDecisionsByFilename || $[16] !== getChapterKeyForFilename || $[17] !== orderedByFilename || $[18] !== statusByFilename || $[19] !== unresolvedThreadCountByFile || $[20] !== viewedFilenames) {
      t9 = (group, groupIndex) => group.files.map((file_1, index) => {
        const orderedFile = orderedByFilename.get(file_1.filename);
        const fileReviewDecisions = fileReviewDecisionsByFilename.get(file_1.filename);
        const reviewDecisionTitle = formatFileReviewDecisionLabel(fileReviewDecisions);
        return {
          filename: file_1.filename,
          basename: getBasename(file_1.filename),
          status: statusByFilename.get(file_1.filename) ?? FILE_STATUS.MODIFIED,
          isViewed: viewedFilenames.has(file_1.filename),
          isChangedSinceLastView: changedSinceLastViewFilenames.has(file_1.filename),
          groupName: group.groupName,
          groupOrder: group.groupOrder,
          chapterKey: getChapterKeyForFilename?.(file_1.filename) ?? getPrV2ChapterKey(group.groupName, group.groupOrder),
          sectionIndex: groupIndex + 1,
          groupNameReason: group.groupNameReason,
          orderNumber: orderedFile?.orderNumber ?? index,
          unresolvedThreads: unresolvedThreadCountByFile.get(file_1.filename) ?? 0,
          hasReviewDecision: Boolean(reviewDecisionTitle),
          reviewDecisionTitle,
          reviewDecisionGlyph: reviewDecisionTitle ? getFileReviewDecisionGlyphChar(fileReviewDecisions) : undefined
        };
      });
      $[14] = changedSinceLastViewFilenames;
      $[15] = fileReviewDecisionsByFilename;
      $[16] = getChapterKeyForFilename;
      $[17] = orderedByFilename;
      $[18] = statusByFilename;
      $[19] = unresolvedThreadCountByFile;
      $[20] = viewedFilenames;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    t8 = groupedCodeContent.flatMap(t9);
    $[5] = changedSinceLastViewFilenames;
    $[6] = fileReviewDecisionsByFilename;
    $[7] = getChapterKeyForFilename;
    $[8] = groupedCodeContent;
    $[9] = orderedByFilename;
    $[10] = statusByFilename;
    $[11] = unresolvedThreadCountByFile;
    $[12] = viewedFilenames;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const records = t8;
  const totalFiles = records.length;
  let t9;
  if ($[22] !== records) {
    t9 = records.filter(_temp6);
    $[22] = records;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const viewedFiles = t9.length;
  let t10;
  if ($[24] !== totalFiles || $[25] !== viewedFiles) {
    t10 = totalFiles > 0 ? Math.round(viewedFiles / totalFiles * 100) : 0;
    $[24] = totalFiles;
    $[25] = viewedFiles;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== totalFiles || $[29] !== viewedFiles) {
    t11 = {
      totalFiles,
      viewedFiles,
      viewedPercent: t10
    };
    $[27] = t10;
    $[28] = totalFiles;
    $[29] = viewedFiles;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const viewedProgress = t11;
  const shouldShowViewedProgress = viewedProgress.totalFiles > 0 && viewedProgress.viewedFiles > 0;
  let t12;
  if ($[31] !== searchQuery) {
    t12 = searchQuery.trim().toLowerCase();
    $[31] = searchQuery;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const searchTerm = t12;
  let t13;
  bb0: {
    if (!searchTerm) {
      t13 = records;
      break bb0;
    }
    let t14;
    if ($[33] !== hasAiFileGrouping || $[34] !== records || $[35] !== searchTerm || $[36] !== viewMode) {
      let t15;
      if ($[38] !== hasAiFileGrouping || $[39] !== searchTerm || $[40] !== viewMode) {
        t15 = record_0 => record_0.filename.toLowerCase().includes(searchTerm) || hasAiFileGrouping && viewMode === "grouped" && record_0.groupName.toLowerCase().includes(searchTerm);
        $[38] = hasAiFileGrouping;
        $[39] = searchTerm;
        $[40] = viewMode;
        $[41] = t15;
      } else {
        t15 = $[41];
      }
      t14 = records.filter(t15);
      $[33] = hasAiFileGrouping;
      $[34] = records;
      $[35] = searchTerm;
      $[36] = viewMode;
      $[37] = t14;
    } else {
      t14 = $[37];
    }
    t13 = t14;
  }
  const filteredRecords = t13;
  const isTreePanelVisible = isDrawerLayout ? isDrawerOpen : !isCollapsed;
  const effectiveFileTreeWidth = clampFileTreeWidth(draggedTreeWidthPx ?? fileTreeWidthPx);
  const shouldShowMobileDrawerTrigger = isDrawerLayout && !toolbarHost && !isDrawerOpen && !isZenMode && records.length > 0;
  let t14;
  if ($[42] !== selectFileAction) {
    t14 = filename => {
      selectFileAction(filename);
      setIsDrawerOpen(false);
    };
    $[42] = selectFileAction;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  const handleSelectFile = t14;
  let t15;
  if ($[44] !== selectGroupAction) {
    t15 = groupId => {
      selectGroupAction(groupId);
      setIsDrawerOpen(false);
    };
    $[44] = selectGroupAction;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  const handleSelectGroup = t15;
  let t16;
  if ($[46] !== selectChapterAction) {
    t16 = chapterKey => {
      selectChapterAction?.(chapterKey);
    };
    $[46] = selectChapterAction;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  const handleSelectChapter = t16;
  let t17;
  if ($[48] !== isComparisonLoading || $[49] !== isDrawerLayout || $[50] !== toggleCollapsed) {
    t17 = () => {
      if (isComparisonLoading) {
        return;
      }
      if (isDrawerLayout) {
        setIsDrawerOpen(_temp7);
        return;
      }
      toggleCollapsed();
    };
    $[48] = isComparisonLoading;
    $[49] = isDrawerLayout;
    $[50] = toggleCollapsed;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  const toggleTreeVisibility = t17;
  let t18;
  let t19;
  if ($[52] !== isSearchVisible || $[53] !== isTreePanelVisible) {
    t18 = () => {
      if (!isSearchVisible || !isTreePanelVisible) {
        return;
      }
      searchInputRef.current?.focus();
    };
    t19 = [isSearchVisible, isTreePanelVisible];
    $[52] = isSearchVisible;
    $[53] = isTreePanelVisible;
    $[54] = t18;
    $[55] = t19;
  } else {
    t18 = $[54];
    t19 = $[55];
  }
  useEffect(t18, t19);
  let t20;
  let t21;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => () => {
      if (releaseDraggedWidthRafRef.current != null) {
        window.cancelAnimationFrame(releaseDraggedWidthRafRef.current);
      }
    };
    t21 = [];
    $[56] = t20;
    $[57] = t21;
  } else {
    t20 = $[56];
    t21 = $[57];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[58] !== applyViewPreferences || $[59] !== fileTreeWidthPx || $[60] !== isCollapsed || $[61] !== isDrawerLayout || $[62] !== isZenMode) {
    t22 = () => {
      if (!isDrawerLayout && !isCollapsed && !isZenMode) {
        const nextWidth = clampFileTreeWidth(fileTreeWidthPx);
        if (nextWidth !== fileTreeWidthPx) {
          applyViewPreferences({
            fileTreeWidthPx: nextWidth
          });
        }
      }
    };
    t23 = [applyViewPreferences, fileTreeWidthPx, isCollapsed, isDrawerLayout, isZenMode];
    $[58] = applyViewPreferences;
    $[59] = fileTreeWidthPx;
    $[60] = isCollapsed;
    $[61] = isDrawerLayout;
    $[62] = isZenMode;
    $[63] = t22;
    $[64] = t23;
  } else {
    t22 = $[63];
    t23 = $[64];
  }
  useEffect(t22, t23);
  const {
    collapsedFolders,
    toggleFolder
  } = usePrV2FileTreeFolderCollapse(searchTerm);
  let t24;
  bb1: {
    if (viewMode === "grouped") {
      let t25;
      if ($[65] !== collapsedFolders || $[66] !== filteredRecords) {
        const recordsByGroup = new Map();
        filteredRecords.forEach(record_1 => {
          const key = record_1.chapterKey;
          const existing = recordsByGroup.get(key);
          if (existing) {
            existing.files.push(record_1);
            return;
          }
          recordsByGroup.set(key, {
            groupId: getPrV2GroupElementId(record_1.groupName, record_1.groupOrder),
            groupName: record_1.groupName,
            groupOrder: record_1.groupOrder,
            chapterKey: record_1.chapterKey,
            sectionIndex: record_1.sectionIndex,
            groupReason: record_1.groupNameReason,
            files: [record_1]
          });
        });
        const sortedGroups = Array.from(recordsByGroup.values()).sort(_temp8);
        let t26;
        if ($[68] !== collapsedFolders) {
          t26 = group_0 => {
            const sortedFiles = group_0.files.slice().sort(_temp9);
            const root = buildTree(sortedFiles);
            const groupRows = [...(group_0.sectionIndex > 1 ? [{
              id: `group-spacer:${group_0.groupId}`,
              kind: "group-spacer",
              sectionIndex: group_0.sectionIndex
            } satisfies GroupSpacerRow] : []), {
              id: `group:${group_0.groupId}`,
              kind: "group",
              groupId: group_0.groupId,
              chapterKey: group_0.chapterKey,
              sectionIndex: group_0.sectionIndex,
              label: group_0.groupName,
              groupReason: group_0.groupReason
            } satisfies GroupRow];
            const visitGroupedNode = (node, parentPath, depth) => {
              node.folders.forEach((childNode, folderName) => {
                const {
                  mergedFolderName,
                  terminalNode
                } = collapseSingleChildFolderChain(folderName, childNode);
                const relativeFolderPath = parentPath ? `${parentPath}/${mergedFolderName}` : mergedFolderName;
                const folderPath = `${group_0.groupId}:${relativeFolderPath}`;
                const isFolderCollapsed = collapsedFolders.has(folderPath);
                groupRows.push({
                  id: `folder:${folderPath}`,
                  kind: "folder",
                  folderPath,
                  fullPath: relativeFolderPath,
                  label: mergedFolderName,
                  depth,
                  isCollapsed: isFolderCollapsed
                });
                if (!isFolderCollapsed) {
                  visitGroupedNode(terminalNode, relativeFolderPath, depth + 1);
                }
              });
              node.files.forEach(record_2 => {
                groupRows.push({
                  id: `file:${record_2.filename}`,
                  kind: "file",
                  filename: record_2.filename,
                  chapterKey: record_2.chapterKey,
                  label: record_2.basename,
                  depth,
                  status: record_2.status,
                  isViewed: record_2.isViewed,
                  isChangedSinceLastView: record_2.isChangedSinceLastView,
                  unresolvedThreads: record_2.unresolvedThreads,
                  hasReviewDecision: record_2.hasReviewDecision,
                  reviewDecisionTitle: record_2.reviewDecisionTitle,
                  reviewDecisionGlyph: record_2.reviewDecisionGlyph
                });
              });
            };
            visitGroupedNode(root, "", 0);
            return groupRows;
          };
          $[68] = collapsedFolders;
          $[69] = t26;
        } else {
          t26 = $[69];
        }
        t25 = sortedGroups.flatMap(t26);
        $[65] = collapsedFolders;
        $[66] = filteredRecords;
        $[67] = t25;
      } else {
        t25 = $[67];
      }
      t24 = t25;
      break bb1;
    }
    let nextRows;
    if ($[70] !== collapsedFolders || $[71] !== filteredRecords) {
      const root_0 = buildTree(filteredRecords);
      sortTreeNode(root_0);
      nextRows = [];
      const visit = (node_0, parentPath_0, depth_0) => {
        node_0.folders.forEach((childNode_0, folderName_0) => {
          const {
            mergedFolderName: mergedFolderName_0,
            terminalNode: terminalNode_0
          } = collapseSingleChildFolderChain(folderName_0, childNode_0);
          const folderPath_0 = parentPath_0 ? `${parentPath_0}/${mergedFolderName_0}` : mergedFolderName_0;
          const isFolderCollapsed_0 = collapsedFolders.has(folderPath_0);
          nextRows.push({
            id: `folder:${folderPath_0}`,
            kind: "folder",
            folderPath: folderPath_0,
            fullPath: folderPath_0,
            label: mergedFolderName_0,
            depth: depth_0,
            isCollapsed: isFolderCollapsed_0
          });
          if (!isFolderCollapsed_0) {
            visit(terminalNode_0, folderPath_0, depth_0 + 1);
          }
        });
        node_0.files.forEach(record_3 => {
          nextRows.push({
            id: `file:${record_3.filename}`,
            kind: "file",
            filename: record_3.filename,
            chapterKey: record_3.chapterKey,
            label: record_3.basename,
            depth: depth_0,
            status: record_3.status,
            isViewed: record_3.isViewed,
            isChangedSinceLastView: record_3.isChangedSinceLastView,
            unresolvedThreads: record_3.unresolvedThreads,
            hasReviewDecision: record_3.hasReviewDecision,
            reviewDecisionTitle: record_3.reviewDecisionTitle,
            reviewDecisionGlyph: record_3.reviewDecisionGlyph
          });
        });
      };
      visit(root_0, "", 0);
      $[70] = collapsedFolders;
      $[71] = filteredRecords;
      $[72] = nextRows;
    } else {
      nextRows = $[72];
    }
    t24 = nextRows;
  }
  const rows = t24;
  let t25;
  if ($[73] !== number || $[74] !== owner || $[75] !== repo) {
    t25 = getPrV2ActiveFileStoreKey(owner, repo, number);
    $[73] = number;
    $[74] = owner;
    $[75] = repo;
    $[76] = t25;
  } else {
    t25 = $[76];
  }
  const activeFileStoreKey = t25;
  let t26;
  if ($[77] !== activeFileStoreKey) {
    t26 = state_2 => state_2.activeFilenameByPr[activeFileStoreKey] ?? null;
    $[77] = activeFileStoreKey;
    $[78] = t26;
  } else {
    t26 = $[78];
  }
  const activeFilename = usePrV2ActiveFileStore(t26);
  const [listApi, setListApi] = useListCallbackRef(null);
  const lastTreeScrollInputAtRef = useRef(Number.NEGATIVE_INFINITY);
  let t27;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = () => {
      lastTreeScrollInputAtRef.current = performance.now();
    };
    $[79] = t27;
  } else {
    t27 = $[79];
  }
  const markTreeScrollInput = t27;
  let t28;
  if ($[80] !== rows) {
    t28 = (api, filename_0) => {
      const index_0 = rows.findIndex(row => row.kind === "file" && row.filename === filename_0);
      if (index_0 >= 0) {
        api.scrollToRow({
          index: index_0,
          align: "smart",
          behavior: "instant"
        });
      }
    };
    $[80] = rows;
    $[81] = t28;
  } else {
    t28 = $[81];
  }
  const revealFileRow = useEffectEvent(t28);
  let t29;
  if ($[82] !== activeFilename || $[83] !== listApi || $[84] !== revealFileRow) {
    t29 = () => {
      if (!listApi) {
        lastTreeScrollInputAtRef.current = Number.NEGATIVE_INFINITY;
        return;
      }
      if (performance.now() - lastTreeScrollInputAtRef.current < TREE_SCROLL_INPUT_HOLD_MS) {
        return;
      }
      revealFileRow(listApi, activeFilename);
    };
    $[82] = activeFilename;
    $[83] = listApi;
    $[84] = revealFileRow;
    $[85] = t29;
  } else {
    t29 = $[85];
  }
  let t30;
  if ($[86] !== activeFilename || $[87] !== listApi) {
    t30 = [listApi, activeFilename];
    $[86] = activeFilename;
    $[87] = listApi;
    $[88] = t30;
  } else {
    t30 = $[88];
  }
  useEffect(t29, t30);
  let t31;
  if ($[89] !== rows || $[90] !== searchTerm) {
    t31 = searchTerm ? rows.flatMap(_temp10) : [];
    $[89] = rows;
    $[90] = searchTerm;
    $[91] = t31;
  } else {
    t31 = $[91];
  }
  const searchMatchFilenames = t31;
  const {
    searchCursor,
    movedCursor,
    handleSearchKeyDown
  } = usePrV2FileTreeSearchCursor(searchMatchFilenames, handleSelectFile);
  let t32;
  if ($[92] !== listApi || $[93] !== movedCursor || $[94] !== revealFileRow) {
    t32 = () => {
      if (listApi && movedCursor) {
        revealFileRow(listApi, movedCursor);
      }
    };
    $[92] = listApi;
    $[93] = movedCursor;
    $[94] = revealFileRow;
    $[95] = t32;
  } else {
    t32 = $[95];
  }
  let t33;
  if ($[96] !== listApi || $[97] !== movedCursor) {
    t33 = [listApi, movedCursor];
    $[96] = listApi;
    $[97] = movedCursor;
    $[98] = t33;
  } else {
    t33 = $[98];
  }
  useEffect(t32, t33);
  let t34;
  if ($[99] !== isComparisonLoading || $[100] !== toggleTreeVisibility) {
    t34 = event => {
      if (isComparisonLoading) {
        return;
      }
      if (isPrV2HotkeyTextEntryTarget(event.target)) {
        return;
      }
      event.preventDefault();
      toggleTreeVisibility();
    };
    $[99] = isComparisonLoading;
    $[100] = toggleTreeVisibility;
    $[101] = t34;
  } else {
    t34 = $[101];
  }
  let t35;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = {
      enableOnFormTags: true,
      preventDefault: false
    };
    $[102] = t35;
  } else {
    t35 = $[102];
  }
  let t36;
  if ($[103] !== isComparisonLoading || $[104] !== isDrawerLayout || $[105] !== toggleTreeVisibility) {
    t36 = [isComparisonLoading, isDrawerLayout, toggleTreeVisibility];
    $[103] = isComparisonLoading;
    $[104] = isDrawerLayout;
    $[105] = toggleTreeVisibility;
    $[106] = t36;
  } else {
    t36 = $[106];
  }
  useHotkeys("f", t34, t35, t36);
  let t37;
  if ($[107] !== isComparisonLoading || $[108] !== isDrawerLayout) {
    t37 = event_0 => {
      if (isComparisonLoading) {
        return;
      }
      event_0.preventDefault();
      if (isDrawerLayout) {
        setIsDrawerOpen(true);
      }
      setIsSearchVisible(true);
    };
    $[107] = isComparisonLoading;
    $[108] = isDrawerLayout;
    $[109] = t37;
  } else {
    t37 = $[109];
  }
  let t38;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = {
      enableOnFormTags: true,
      preventDefault: true
    };
    $[110] = t38;
  } else {
    t38 = $[110];
  }
  let t39;
  if ($[111] !== isComparisonLoading || $[112] !== isDrawerLayout) {
    t39 = [isComparisonLoading, isDrawerLayout];
    $[111] = isComparisonLoading;
    $[112] = isDrawerLayout;
    $[113] = t39;
  } else {
    t39 = $[113];
  }
  useHotkeys("mod+shift+f", t37, t38, t39);
  let t40;
  if ($[114] !== isComparisonLoading || $[115] !== isSearchVisible || $[116] !== searchQuery) {
    t40 = event_1 => {
      if (isComparisonLoading) {
        return;
      }
      if (!isSearchVisible) {
        return;
      }
      event_1.preventDefault();
      const activeElement = document.activeElement as HTMLElement | null;
      if (activeElement && activeElement === searchInputRef.current) {
        activeElement.blur();
      }
      if (!searchQuery) {
        setIsSearchVisible(false);
      } else {
        setSearchQuery("");
      }
    };
    $[114] = isComparisonLoading;
    $[115] = isSearchVisible;
    $[116] = searchQuery;
    $[117] = t40;
  } else {
    t40 = $[117];
  }
  let t41;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = {
      enableOnFormTags: true,
      preventDefault: false
    };
    $[118] = t41;
  } else {
    t41 = $[118];
  }
  let t42;
  if ($[119] !== isComparisonLoading || $[120] !== isSearchVisible || $[121] !== searchQuery) {
    t42 = [isComparisonLoading, isSearchVisible, searchQuery];
    $[119] = isComparisonLoading;
    $[120] = isSearchVisible;
    $[121] = searchQuery;
    $[122] = t42;
  } else {
    t42 = $[122];
  }
  useHotkeys("esc", t40, t41, t42);
  let t43;
  if ($[123] !== activeChapterKey || $[124] !== activeFileStoreKey || $[125] !== chapterModeEnabled || $[126] !== handleSelectChapter || $[127] !== handleSelectFile || $[128] !== handleSelectGroup || $[129] !== isComparisonLoading || $[130] !== rows || $[131] !== searchCursor || $[132] !== toggleFolder) {
    t43 = {
      rows,
      activeFileStoreKey,
      searchCursorFilename: searchCursor,
      isInteractionDisabled: isComparisonLoading,
      chapterModeEnabled,
      activeChapterKey,
      onSelectFile: handleSelectFile,
      onSelectGroup: handleSelectGroup,
      onSelectChapter: handleSelectChapter,
      toggleFolder
    };
    $[123] = activeChapterKey;
    $[124] = activeFileStoreKey;
    $[125] = chapterModeEnabled;
    $[126] = handleSelectChapter;
    $[127] = handleSelectFile;
    $[128] = handleSelectGroup;
    $[129] = isComparisonLoading;
    $[130] = rows;
    $[131] = searchCursor;
    $[132] = toggleFolder;
    $[133] = t43;
  } else {
    t43 = $[133];
  }
  const virtualizedRowProps = t43;
  let t44;
  if ($[134] !== isComparisonLoading || $[135] !== isSearchVisible) {
    t44 = () => {
      if (isComparisonLoading) {
        return;
      }
      if (isSearchVisible) {
        setSearchQuery("");
      }
      setIsSearchVisible(!isSearchVisible);
    };
    $[134] = isComparisonLoading;
    $[135] = isSearchVisible;
    $[136] = t44;
  } else {
    t44 = $[136];
  }
  const handleToggleSearch = t44;
  let t45;
  if ($[137] !== hasMeaningfulComparison || $[138] !== hasUserComparisonOverride || $[139] !== isComparisonLoading || $[140] !== lastReviewedSubmittedAt || $[141] !== lastReviewedVersionSha || $[142] !== resetComparisonAction || $[143] !== selectedBaseSha || $[144] !== selectedHeadSha || $[145] !== setComparisonAction || $[146] !== versionOptions) {
    t45 = () => <PrV2VersionComparisonPicker versionOptions={versionOptions} selectedBaseSha={selectedBaseSha} selectedHeadSha={selectedHeadSha} isComparisonLoading={isComparisonLoading} hasMeaningfulComparison={hasMeaningfulComparison} hasUserComparisonOverride={hasUserComparisonOverride} lastReviewedVersionSha={lastReviewedVersionSha} lastReviewedSubmittedAt={lastReviewedSubmittedAt} setComparisonAction={setComparisonAction} resetComparisonAction={resetComparisonAction} />;
    $[137] = hasMeaningfulComparison;
    $[138] = hasUserComparisonOverride;
    $[139] = isComparisonLoading;
    $[140] = lastReviewedSubmittedAt;
    $[141] = lastReviewedVersionSha;
    $[142] = resetComparisonAction;
    $[143] = selectedBaseSha;
    $[144] = selectedHeadSha;
    $[145] = setComparisonAction;
    $[146] = versionOptions;
    $[147] = t45;
  } else {
    t45 = $[147];
  }
  const renderComparisonPicker = t45;
  let t46;
  if ($[148] !== rows.length || $[149] !== setListApi || $[150] !== virtualizedRowProps) {
    t46 = rows.length === 0 ? <div className="px-4 py-3.5 text-zinc-500">No files match the current filters.</div> : <List defaultHeight={TREE_DEFAULT_HEIGHT_PX} listRef={setListApi} overscanCount={10} role="list" rowComponent={VirtualizedRow} rowCount={rows.length} rowHeight={getPrV2FileTreeVirtualRowHeight} rowKey={getPrV2FileTreeRowKey} rowProps={virtualizedRowProps} className="custom-scrollbar py-2" style={{
      height: "100%",
      width: "100%",
      scrollbarGutter: "stable"
    }} onTouchMove={markTreeScrollInput} onWheel={markTreeScrollInput} />;
    $[148] = rows.length;
    $[149] = setListApi;
    $[150] = virtualizedRowProps;
    $[151] = t46;
  } else {
    t46 = $[151];
  }
  const fileTreeList = t46;
  let t47;
  if ($[152] !== files.length || $[153] !== handleToggleSearch || $[154] !== hasAiFileGrouping || $[155] !== hasComparisonPicker || $[156] !== isComparisonLoading || $[157] !== isOrderedFilesLoading || $[158] !== isSearchVisible || $[159] !== renderComparisonPicker || $[160] !== toggleViewMode || $[161] !== viewMode) {
    t47 = args => {
      const groupedModeLabel = viewMode === "grouped" && !hasAiFileGrouping ? isOrderedFilesLoading ? "Grouping" : "Files" : "Grouped";
      const groupedModeTooltip = viewMode === "grouped" && !hasAiFileGrouping ? isOrderedFilesLoading ? "cubic is clustering changed files and computing the review order. Files are shown in their current order until grouping is ready." : "Files are shown in their current order because AI grouping is not ready for this comparison." : "Grouped mode uses cubic to cluster changed files and order them in an optimal review sequence.";
      return <div className={cn("flex min-h-9 flex-col justify-center gap-1.5 px-2 py-1", args?.toolbarClassName)}><div className="flex items-center gap-1"><BetterTooltip content={<div className="max-w-[260px] text-xs leading-relaxed">{viewMode === "grouped" ? groupedModeTooltip : "Switch to Grouped mode to let cubic cluster changed files and order them in an optimal review sequence."}</div>}><button type="button" className={PR_V2_TOOLBAR_TEXT_BUTTON_CLASSNAME} disabled={isComparisonLoading} onClick={toggleViewMode} aria-label="Toggle file tree view mode">{viewMode === "grouped" ? groupedModeLabel : "Tree"}{files.length > 0 && <span className="ml-1 text-[9px] opacity-70">{`(${files.length})`}</span>}</button></BetterTooltip>{isOrderedFilesLoading ? <BetterTooltip content={<div className="max-w-[260px] text-xs leading-relaxed">cubic is clustering changes and computing the review order for grouped mode. The file list updates when it is ready.</div>} side="bottom"><div data-testid="pr-v2-ordered-files-loading-indicator" className="inline-flex cursor-default items-center rounded px-1 text-zinc-500"><WorkingDots ariaLabel="cubic is computing grouped file order" /></div></BetterTooltip> : null}{renderComparisonPicker()}<button type="button" className={cn(PR_V2_TOOLBAR_ICON_BUTTON_CLASSNAME, !hasComparisonPicker && "ml-auto", isSearchVisible && "bg-zinc-900 text-zinc-100", isComparisonLoading && "cursor-not-allowed opacity-60 hover:bg-transparent")} disabled={isComparisonLoading} onClick={handleToggleSearch} aria-label="Toggle file search"><Search className="size-3.5" /></button>{args?.trailingControls ?? null}</div></div>;
    };
    $[152] = files.length;
    $[153] = handleToggleSearch;
    $[154] = hasAiFileGrouping;
    $[155] = hasComparisonPicker;
    $[156] = isComparisonLoading;
    $[157] = isOrderedFilesLoading;
    $[158] = isSearchVisible;
    $[159] = renderComparisonPicker;
    $[160] = toggleViewMode;
    $[161] = viewMode;
    $[162] = t47;
  } else {
    t47 = $[162];
  }
  const renderFileTreeToolbar = t47;
  let t48;
  if ($[163] !== applyViewPreferences || $[164] !== effectiveFileTreeWidth || $[165] !== fileTreeList || $[166] !== handleSearchKeyDown || $[167] !== isCollapsed || $[168] !== isComparisonLoading || $[169] !== isDesktopRightSide || $[170] !== isDrawerLayout || $[171] !== isDrawerOpen || $[172] !== isSearchVisible || $[173] !== isTreePanelVisible || $[174] !== isZenMode || $[175] !== layout || $[176] !== renderComparisonPicker || $[177] !== renderFileTreeToolbar || $[178] !== searchQuery || $[179] !== shouldShowMobileDrawerTrigger || $[180] !== shouldShowViewedProgress || $[181] !== toggleTreeVisibility || $[182] !== toolbarHost || $[183] !== versionUpdate || $[184] !== viewedProgress.totalFiles || $[185] !== viewedProgress.viewedFiles || $[186] !== viewedProgress.viewedPercent) {
    const renderFileTreeSecondaryControls = () => versionUpdate || shouldShowViewedProgress || isSearchVisible ? <div data-testid="pr-v2-file-tree-header" className="relative z-20 flex shrink-0 flex-col gap-2.5 bg-background px-2 pt-2">{versionUpdate ? <div data-testid="pr-v2-file-tree-version-update" className="flex min-w-0 items-center justify-between gap-2 px-1.5 py-0.5"><div className="flex min-w-0 items-center gap-1.5 text-[10px] text-zinc-500"><span className="size-1.5 shrink-0 rounded-full bg-zinc-500/70" aria-hidden={true} /><span className="min-w-0 truncate text-zinc-400">{`${versionUpdate.latestLabel} available`}</span><span className="shrink-0 text-zinc-700" aria-hidden={true}>/</span><span className="min-w-0 truncate">{`Viewing ${versionUpdate.currentLabel}`}</span></div><button type="button" data-testid="pr-v2-file-tree-review-latest" className="h-5 shrink-0 rounded px-1.5 text-[10px] font-medium text-amber-300/80 hover:bg-amber-400/10 hover:text-amber-200 disabled:cursor-not-allowed disabled:opacity-60" disabled={isComparisonLoading} onClick={versionUpdate.onReviewLatest} aria-label={`Refresh to latest version ${versionUpdate.latestLabel}`}>Refresh</button></div> : null}{shouldShowViewedProgress ? <div className="px-1.5"><div className="mb-1 text-[10px] font-medium text-zinc-500">{`${viewedProgress.viewedFiles}/${viewedProgress.totalFiles} viewed`}</div><Progress value={viewedProgress.viewedPercent} aria-label="Viewed files progress" aria-valuetext={`${viewedProgress.viewedFiles} of ${viewedProgress.totalFiles} files viewed`} className="h-1 bg-zinc-900" /></div> : null}{isSearchVisible ? <div className="flex items-center gap-1 rounded border border-zinc-800 bg-zinc-900 px-2 py-1"><Search className="size-3.5 text-zinc-500" /><input ref={searchInputRef} type="text" value={searchQuery} disabled={isComparisonLoading} onChange={event_2 => setSearchQuery(event_2.target.value)} onKeyDown={handleSearchKeyDown} placeholder="Search files" className="w-full bg-transparent text-xs text-zinc-200 outline-hidden placeholder:text-zinc-500" />{searchQuery ? <button type="button" className="rounded p-0.5 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200" disabled={isComparisonLoading} onClick={() => setSearchQuery("")} aria-label="Clear file search"><X className="size-3.5" /></button> : null}</div> : null}</div> : null;
    const renderFileTreeRows = () => <div className="flex min-h-0 flex-1 flex-col bg-background">{renderFileTreeSecondaryControls()}<div className="relative z-0 min-h-0 flex-1 overflow-hidden" aria-busy={isComparisonLoading} style={{
        fontSize: `${PR_V2_FILE_TREE_FONT_SIZE_PX}px`
      }}>{fileTreeList}{isComparisonLoading ? <div data-testid="pr-v2-file-tree-stale-overlay" className="absolute inset-0 z-10 flex items-center justify-center bg-background/72 backdrop-blur-[1px]"><div className="flex items-center gap-2 rounded-full border border-zinc-800 bg-background/90 px-3 py-1.5 text-[11px] font-medium text-zinc-300 shadow-lg"><WorkingDots /><span>Loading selected comparison</span></div></div> : null}</div></div>;
    const renderFileTreePanel = args_0 => <div className="flex min-h-0 flex-1 flex-col bg-background">{args_0?.showToolbar !== false ? renderFileTreeToolbar(args_0) : null}{renderFileTreeRows()}</div>;
    const drawerContent = renderFileTreePanel({
      toolbarClassName: "pr-10"
    });
    const t49 = !isTreePanelVisible;
    let t50;
    if ($[188] !== isComparisonLoading || $[189] !== t49 || $[190] !== toggleTreeVisibility) {
      t50 = <FileTreeToggle isCollapsed={t49} isDisabled={isComparisonLoading} onToggle={toggleTreeVisibility} />;
      $[188] = isComparisonLoading;
      $[189] = t49;
      $[190] = toggleTreeVisibility;
      $[191] = t50;
    } else {
      t50 = $[191];
    }
    const fileTreeToggle = t50;
    const isCondensedToolbar = isDrawerLayout || isCollapsed;
    let t51;
    if ($[192] !== effectiveFileTreeWidth || $[193] !== fileTreeToggle || $[194] !== isCondensedToolbar || $[195] !== isDrawerLayout || $[196] !== isZenMode || $[197] !== renderComparisonPicker || $[198] !== renderFileTreeToolbar || $[199] !== toolbarHost) {
      t51 = toolbarHost && !isZenMode ? createPortal(<div ref={desktopToolbarRef} data-testid={isDrawerLayout ? "pr-v2-file-tree-drawer-toolbar" : "pr-v2-file-tree-desktop-toolbar"} className={cn("bg-background", isDrawerLayout ? "flex" : "hidden md:flex", isCondensedToolbar ? "h-9 w-max items-center justify-end gap-1 pr-2 pl-1" : "flex-col")} style={{
        width: isCondensedToolbar ? undefined : effectiveFileTreeWidth
      }}>{isCondensedToolbar ? <>{!isDrawerLayout ? renderComparisonPicker() : null}{fileTreeToggle}</> : renderFileTreeToolbar({
          trailingControls: fileTreeToggle
        })}</div>, toolbarHost) : null;
      $[192] = effectiveFileTreeWidth;
      $[193] = fileTreeToggle;
      $[194] = isCondensedToolbar;
      $[195] = isDrawerLayout;
      $[196] = isZenMode;
      $[197] = renderComparisonPicker;
      $[198] = renderFileTreeToolbar;
      $[199] = toolbarHost;
      $[200] = t51;
    } else {
      t51 = $[200];
    }
    const toolbar = t51;
    let t52;
    if ($[201] !== applyViewPreferences || $[202] !== effectiveFileTreeWidth || $[203] !== isCollapsed || $[204] !== isComparisonLoading || $[205] !== isDesktopRightSide || $[206] !== isZenMode) {
      t52 = event_3 => {
        if (event_3.button !== 0 || isCollapsed || isZenMode || isComparisonLoading) {
          return;
        }
        event_3.preventDefault();
        const startX = event_3.clientX;
        const startWidth = effectiveFileTreeWidth;
        const resizeHandle = event_3.currentTarget;
        if (releaseDraggedWidthRafRef.current != null) {
          window.cancelAnimationFrame(releaseDraggedWidthRafRef.current);
          releaseDraggedWidthRafRef.current = null;
        }
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        const dragAbortController = new AbortController();
        const endDrag = () => {
          dragAbortController.abort();
          const finalWidth = clampFileTreeWidth(resizeWidthRef.current ?? startWidth);
          resizeWidthRef.current = null;
          setDraggedTreeWidthPx(finalWidth);
          applyViewPreferences({
            fileTreeWidthPx: finalWidth
          });
          releaseDraggedWidthRafRef.current = window.requestAnimationFrame(() => {
            setDraggedTreeWidthPx(null);
            releaseDraggedWidthRafRef.current = null;
          });
          document.body.style.cursor = "";
          document.body.style.userSelect = "";
        };
        const onPointerMove = moveEvent => {
          if (!resizeHandle.isConnected) {
            endDrag();
            return;
          }
          const delta = isDesktopRightSide ? startX - moveEvent.clientX : moveEvent.clientX - startX;
          const nextWidth_0 = clampFileTreeWidth(startWidth + delta);
          resizeWidthRef.current = nextWidth_0;
          const nextWidthPx = `${nextWidth_0}px`;
          if (treeWidthWrapperRef.current) {
            treeWidthWrapperRef.current.style.width = nextWidthPx;
          }
          if (desktopToolbarRef.current) {
            desktopToolbarRef.current.style.width = nextWidthPx;
          }
        };
        const {
          signal
        } = dragAbortController;
        document.addEventListener("pointermove", onPointerMove, {
          signal
        });
        document.addEventListener("pointerup", endDrag, {
          signal
        });
        document.addEventListener("pointercancel", endDrag, {
          signal
        });
      };
      $[201] = applyViewPreferences;
      $[202] = effectiveFileTreeWidth;
      $[203] = isCollapsed;
      $[204] = isComparisonLoading;
      $[205] = isDesktopRightSide;
      $[206] = isZenMode;
      $[207] = t52;
    } else {
      t52 = $[207];
    }
    const handleResizePointerDown = t52;
    let t53;
    if ($[208] !== shouldShowMobileDrawerTrigger) {
      t53 = shouldShowMobileDrawerTrigger ? <button type="button" aria-label="Open file tree" data-testid="pr-v2-mobile-file-tree-trigger" className="fixed right-6 bottom-[calc(var(--pr-v2-mobile-action-bar-height,0px)+1.5rem)] z-40 inline-flex items-center gap-2 rounded-full border border-border bg-background/95 px-4 py-2 text-sm font-medium text-foreground shadow-lg backdrop-blur hover:bg-secondary" onClick={() => setIsDrawerOpen(true)}><Folder className="size-3.5" /><span>Files</span></button> : null;
      $[208] = shouldShowMobileDrawerTrigger;
      $[209] = t53;
    } else {
      t53 = $[209];
    }
    const t54 = isDrawerLayout && isDrawerOpen;
    const t55 = layout === "right-drawer" ? "right" : "left";
    let t56;
    if ($[210] === Symbol.for("react.memo_cache_sentinel")) {
      t56 = <SheetTitle className="sr-only">Changed files</SheetTitle>;
      $[210] = t56;
    } else {
      t56 = $[210];
    }
    let t57;
    if ($[211] !== drawerContent || $[212] !== t55) {
      t57 = <SheetContent side={t55} data-testid="pr-v2-file-tree-mobile-drawer" className="flex h-full w-[min(22rem,85vw)] max-w-none flex-col border-border bg-background p-0 text-foreground [&>button]:text-muted-foreground">{t56}{drawerContent}</SheetContent>;
      $[211] = drawerContent;
      $[212] = t55;
      $[213] = t57;
    } else {
      t57 = $[213];
    }
    let t58;
    if ($[214] !== t54 || $[215] !== t57) {
      t58 = <Sheet open={t54} onOpenChange={setIsDrawerOpen}>{t57}</Sheet>;
      $[214] = t54;
      $[215] = t57;
      $[216] = t58;
    } else {
      t58 = $[216];
    }
    t48 = <>{toolbar}{t53}{t58}{!isDrawerLayout ? <div className={cn("relative hidden shrink-0 md:block", isCollapsed ? "overflow-visible" : "overflow-x-clip")}>{isCollapsed && !isZenMode && toolbarHost == null ? <div className={cn("absolute -top-[51px] z-10", isDesktopRightSide ? "right-2" : "left-2")}>{fileTreeToggle}</div> : null}<div ref={treeWidthWrapperRef} className={cn("h-full", (isZenMode || isCollapsed) && "overflow-hidden")} style={{
          width: isZenMode || isCollapsed ? 0 : effectiveFileTreeWidth
        }}>{!isZenMode && !isCollapsed ? <><aside className={cn("sticky top-9 h-[calc(100vh-96px)] shrink-0 overflow-hidden bg-background md:flex md:flex-col")} style={{
              width: "100%"
            }}>{renderFileTreePanel({
                showToolbar: toolbarHost == null,
                trailingControls: toolbarHost == null ? fileTreeToggle : undefined
              })}</aside><button type="button" aria-label="Resize file tree" data-testid="pr-v2-file-tree-resize-handle" className={cn("absolute top-0 z-20 hidden h-full w-2 cursor-col-resize bg-transparent hover:bg-zinc-800/60 md:block", isDesktopRightSide ? "-left-1" : "-right-1")} onPointerDown={handleResizePointerDown} /></> : null}</div></div> : null}</>;
    $[163] = applyViewPreferences;
    $[164] = effectiveFileTreeWidth;
    $[165] = fileTreeList;
    $[166] = handleSearchKeyDown;
    $[167] = isCollapsed;
    $[168] = isComparisonLoading;
    $[169] = isDesktopRightSide;
    $[170] = isDrawerLayout;
    $[171] = isDrawerOpen;
    $[172] = isSearchVisible;
    $[173] = isTreePanelVisible;
    $[174] = isZenMode;
    $[175] = layout;
    $[176] = renderComparisonPicker;
    $[177] = renderFileTreeToolbar;
    $[178] = searchQuery;
    $[179] = shouldShowMobileDrawerTrigger;
    $[180] = shouldShowViewedProgress;
    $[181] = toggleTreeVisibility;
    $[182] = toolbarHost;
    $[183] = versionUpdate;
    $[184] = viewedProgress.totalFiles;
    $[185] = viewedProgress.viewedFiles;
    $[186] = viewedProgress.viewedPercent;
    $[187] = t48;
  } else {
    t48 = $[187];
  }
  return t48;
}
function _temp10(row_0) {
  return row_0.kind === "file" ? [row_0.filename] : [];
}
function _temp9(a_0, b_0) {
  if (a_0.orderNumber !== b_0.orderNumber) {
    return a_0.orderNumber - b_0.orderNumber;
  }
  return a_0.filename.localeCompare(b_0.filename);
}
function _temp8(a, b) {
  if (a.groupOrder !== b.groupOrder) {
    return a.groupOrder - b.groupOrder;
  }
  return a.groupName.localeCompare(b.groupName);
}
function _temp7(current) {
  return !current;
}
function _temp6(record) {
  return record.isViewed;
}
function _temp5(file_0) {
  return [file_0.filename, file_0];
}
function _temp4(file) {
  return [file.filename, file.status];
}
function _temp3(state_1) {
  return state_1.applyPreferences;
}
function _temp2(state_0) {
  return state_0.fileTreeWidthPx;
}
function _temp(state) {
  return state.isZenMode;
}